import { useCallback } from 'react';
import { useQueryClient, useMutation, InfiniteData } from '@tanstack/react-query';
import { WorkOrder, Technician } from '@/types';

type OrdersPage = { orders: WorkOrder[]; total: number; nextCursor?: number | null };
import { DataService, bustDashboardStatsCache } from '@/services/dataService';
import { useToast } from '@/contexts/ToastContext';
import { useOffline } from '@/contexts/OfflineContext';
import { enqueueMutation } from '@/offline/queue';
import { queryKeys } from '@/hooks/queries/queryKeys';
import { getOfflineCache, setOfflineCache } from '@/lib/offlineCache';

/**
 * Hooks con mutaciones de órdenes (P0.2/P2.3).
 * P2.2: Si no hay red, encola y actualiza caché optimísticamente; al recuperar red se sincroniza.
 */
export function useOrderMutations(
    companyId: string,
    orders: WorkOrder[],
    technicians: Technician[]
) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { isOnline } = useOffline();

    const invalidateOrders = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
    }, [queryClient]);
    const invalidateStats = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
    }, [queryClient]);

    const _rollback = (previousOrdersQueries: [any, any][]) => {
        previousOrdersQueries.forEach(([queryKey, oldData]) => {
            queryClient.setQueryData(queryKey, oldData);
        });
    };

    const createOrderMutation = useMutation({
        mutationFn: async (newOrder: WorkOrder) => {
            if (!companyId) {
                console.error("CRITICAL: Intentando crear orden sin companyId", newOrder);
                throw new Error("No se puede crear el parte de trabajo: falta identificador de empresa. Intenta refrescar.");
            }
            // V4.228: Respetar el ID generado por la app si existe
            const finalId = newOrder.id || `temp-${Date.now()}`;
            const optimisticOrder = { ...newOrder, id: finalId, companyId };
            return DataService.createOrder(optimisticOrder);
        },
        onMutate: async (newOrder) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.orders() });
            const previousOrdersQueries = queryClient.getQueriesData({ queryKey: queryKeys.orders() });

            // V4.226: Respetar el ID si ya viene asignado (para evitar desincronización con la navegación)
            const finalId = newOrder.id || `temp-${Date.now()}`;
            const optimisticOrder = { ...newOrder, id: finalId, companyId };

            // Añadir optimísticamente solo a la query de activas
            queryClient.setQueryData([...queryKeys.orders(), { isArchived: false }], (oldData: InfiniteData<OrdersPage> | undefined) => {
                if (!oldData || !oldData.pages) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page: OrdersPage, index: number) => {
                        if (index === 0) {
                            return {
                                ...page,
                                orders: [optimisticOrder, ...page.orders]
                            };
                        }
                        return page;
                    })
                };
            });

            return { previousOrdersQueries };
        },
        onError: (err, newOrder, context) => {
            console.error('Failed to create order in DB', err);
            if (context?.previousOrdersQueries) {
                _rollback(context.previousOrdersQueries);
            }
            showToast('Error al crear la orden. Revirtiendo cambios.', 'error');
        },
        onSettled: () => {
            bustDashboardStatsCache();
            invalidateStats();
            invalidateOrders();
        }
    });

    const updateOrderMutation = useMutation({
        mutationFn: async (updatedOrder: WorkOrder) => {
            if (!companyId) {
                throw new Error("No se puede actualizar el parte de trabajo: falta identificador de empresa.");
            }
            if (updatedOrder.id.startsWith('temp-')) {
                 console.warn("WARNING: Updating a temporary order directly to database. This will fail if not yet inserted.", updatedOrder.id);
            }
            return DataService.updateOrder(updatedOrder);
        },
        onMutate: async (updatedOrder) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.orders() });
            const previousOrdersQueries = queryClient.getQueriesData({ queryKey: queryKeys.orders() });

            // Actualizar optimísticamente
            queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                if (!oldData || !oldData.pages) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page: OrdersPage) => ({
                        ...page,
                        orders: page.orders.map((order: WorkOrder) =>
                            order.id === updatedOrder.id ? { ...order, ...updatedOrder } : order
                        ),
                    })),
                };
            });

            return { previousOrdersQueries };
        },
        onError: (err, updatedOrder, context) => {
            console.error('Failed to update order in DB', err);
            if (context?.previousOrdersQueries) {
                _rollback(context.previousOrdersQueries);
            }
            showToast('Error al actualizar la orden. Revirtiendo cambios.', 'error');
        },
        onSettled: () => {
            bustDashboardStatsCache();
            invalidateStats();
            invalidateOrders();
        }
    });

    const deleteOrderMutation = useMutation({
        mutationFn: async (orderId: string) => {
            return DataService.deleteOrder(orderId);
        },
        onMutate: async (orderId) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.orders() });
            const previousOrdersQueries = queryClient.getQueriesData({ queryKey: queryKeys.orders() });

            queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                if (!oldData || !oldData.pages) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page: OrdersPage) => ({
                        ...page,
                        orders: page.orders.filter((order: WorkOrder) => order.id !== orderId),
                    })),
                };
            });

            return { previousOrdersQueries };
        },
        onError: (err, orderId, context) => {
            console.error('Failed to delete order in DB', err);
            if (context?.previousOrdersQueries) {
                _rollback(context.previousOrdersQueries);
            }
            showToast('Error al eliminar la orden. Revirtiendo cambios.', 'error');
        },
        onSettled: () => {
            bustDashboardStatsCache();
            invalidateStats();
            invalidateOrders();
        }
    });

    const deleteOrdersMutation = useMutation({
        mutationFn: async (orderIds: string[]) => {
            await Promise.all(orderIds.map((id) => DataService.deleteOrder(id)));
        },
        onMutate: async (orderIds) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.orders() });
            const previousOrdersQueries = queryClient.getQueriesData({ queryKey: queryKeys.orders() });

            queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                if (!oldData || !oldData.pages) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page: OrdersPage) => ({
                        ...page,
                        orders: page.orders.filter((order: WorkOrder) => !orderIds.includes(order.id)),
                    })),
                };
            });

            return { previousOrdersQueries };
        },
        onError: (err, orderIds, context) => {
            console.error('Failed to batch delete orders', err);
            if (context?.previousOrdersQueries) {
                _rollback(context.previousOrdersQueries);
            }
            showToast('Error al eliminar órdenes. Revirtiendo cambios.', 'error');
        },
        onSettled: () => {
            bustDashboardStatsCache();
            invalidateStats();
            invalidateOrders();
            showToast('Órdenes eliminadas', 'success');
        }
    });

    const deleteEventMutation = useMutation({
        mutationFn: async ({ eventId }: { orderId: string, eventId: string }) => {
            return DataService.deleteEvent(eventId);
        },
        onMutate: async ({ orderId, eventId }) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.orders() });
            const previousOrdersQueries = queryClient.getQueriesData({ queryKey: queryKeys.orders() });

            queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                if (!oldData || !oldData.pages) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page: OrdersPage) => ({
                        ...page,
                        orders: page.orders.map((order: WorkOrder) =>
                            order.id === orderId
                                ? { ...order, events: (order.events || []).filter(e => e.id !== eventId) }
                                : order
                        ),
                    })),
                };
            });

            return { previousOrdersQueries };
        },
        onError: (err, variables, context) => {
            console.error('Failed to delete event from DB', err);
            if (context?.previousOrdersQueries) {
                _rollback(context.previousOrdersQueries);
            }
            showToast('Error al eliminar evento. Revirtiendo.', 'error');
        },
        onSettled: () => {
            bustDashboardStatsCache();
            invalidateStats();
            invalidateOrders();
        }
    });

    const updateEventMutation = useMutation({
        mutationFn: async ({ eventId, newText }: { orderId: string, eventId: string, newText: string }) => {
            return DataService.updateEvent(eventId, newText);
        },
        onMutate: async ({ orderId, eventId, newText }) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.orders() });
            const previousOrdersQueries = queryClient.getQueriesData({ queryKey: queryKeys.orders() });

            queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                if (!oldData || !oldData.pages) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page: OrdersPage) => ({
                        ...page,
                        orders: page.orders.map((order: WorkOrder) =>
                            order.id === orderId
                                ? { ...order, events: (order.events || []).map(e => e.id === eventId ? { ...e, text: newText } : e) }
                                : order
                        ),
                    })),
                };
            });

            return { previousOrdersQueries };
        },
        onError: (err, variables, context) => {
            console.error('Failed to update event in DB', err);
            if (context?.previousOrdersQueries) {
                _rollback(context.previousOrdersQueries);
            }
            showToast('Error al actualizar evento. Revirtiendo.', 'error');
        },
        onSettled: () => {
            bustDashboardStatsCache();
            invalidateStats();
            invalidateOrders();
        }
    });

    const assignOrder = useCallback(
        async (orderId: string, technicianId: string | null) => {
            // 1. Try local orders array first (fast path)
            let order = orders.find((o) => o.id === orderId);

            // 2. Fallback: search ALL React Query cache pages (handles paginated data)
            if (!order) {
                const allCachedQueries = queryClient.getQueriesData<InfiniteData<OrdersPage>>({ queryKey: queryKeys.orders() });
                for (const [, cachedData] of allCachedQueries) {
                    if (cachedData?.pages) {
                        for (const page of cachedData.pages) {
                            const found = (page.orders as WorkOrder[])?.find((o) => o.id === orderId);
                            if (found) { order = found; break; }
                        }
                    }
                    if (order) break;
                }
            }

            // 3. Last resort: fetch from DB directly
            if (!order) {
                try {
                    order = (await DataService.getOrderById(orderId)) ?? undefined;
                } catch (e) {
                    console.error('[assignOrder] Could not fetch order from DB:', e);
                }
            }

            if (!order) {
                console.error('[assignOrder] Order not found anywhere:', orderId);
                showToast('Error: no se encontró la orden para asignar.', 'error');
                return;
            }

            const tech = technicians.find((t) => t.id === technicianId);
            const updatedOrder = {
                ...order,
                technicianId,
                technicianNameSnapshot: tech ? tech.name : order.technicianNameSnapshot,
            };
            await updateOrderMutation.mutateAsync(updatedOrder);
        },
        [orders, technicians, updateOrderMutation, queryClient, showToast]
    );

    // P2.2: Cuando no hay red, encolar y aplicar optimista; no llamar al servidor
    const applyCreateOrderOptimistic = useCallback(
        (optimisticOrder: WorkOrder) => {
            queryClient.setQueryData([...queryKeys.orders(), { isArchived: false }], (oldData: InfiniteData<OrdersPage> | undefined) => {
                if (!oldData || !oldData.pages) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page: OrdersPage, index: number) =>
                        index === 0
                            ? { ...page, orders: [optimisticOrder, ...page.orders] }
                            : page
                    ),
                };
            });
        },
        [queryClient]
    );

    const createOrder = useCallback(
        async (newOrder: WorkOrder) => {
            if (!isOnline) {
                try {
                    const finalId = newOrder.id || `temp-${Date.now()}`;
                    const orderWithCompany = { ...newOrder, id: finalId, companyId };
                    await enqueueMutation('createOrder', { order: orderWithCompany });
                    const optimisticOrder = { ...newOrder, id: finalId, companyId };
                    applyCreateOrderOptimistic(optimisticOrder);

                    // V_OFFLINE_CACHE: Persist new order in local storage cache
                    const cached = await getOfflineCache('pd_cached_orders_active');
                    if (cached) {
                        cached.orders = [optimisticOrder, ...(cached.orders || [])];
                        cached.total = (cached.total || 0) + 1;
                        await setOfflineCache('pd_cached_orders_active', cached);
                    }

                    showToast('Guardado localmente. Se enviará al recuperar conexión.', 'info');
                } catch (e) {
                    console.error('Error al encolar mutación offline:', e);
                    showToast('Sin conexión. No se ha podido guardar localmente.', 'error');
                }
                return;
            }
            await createOrderMutation.mutateAsync(newOrder);
            showToast('Orden creada', 'success');
        },
        [isOnline, companyId, createOrderMutation, showToast, applyCreateOrderOptimistic]
    );

    const updateOrder = useCallback(
        async (updatedOrder: WorkOrder) => {
            if (!isOnline) {
                try {
                    await enqueueMutation('updateOrder', { order: updatedOrder });
                    queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                        if (!oldData?.pages) return oldData;
                        return {
                            ...oldData,
                            pages: oldData.pages.map((page: OrdersPage) => ({
                                ...page,
                                orders: page.orders.map((o: WorkOrder) =>
                                    o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o
                                ),
                            })),
                        };
                    });

                    // V_OFFLINE_CACHE: Persist updated order in local storage cache (both active and archived)
                    const cachedActive = await getOfflineCache('pd_cached_orders_active');
                    if (cachedActive && cachedActive.orders) {
                        cachedActive.orders = cachedActive.orders.map((o: WorkOrder) =>
                            o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o
                        );
                        await setOfflineCache('pd_cached_orders_active', cachedActive);
                    }
                    const cachedArchived = await getOfflineCache('pd_cached_orders_archived');
                    if (cachedArchived && cachedArchived.orders) {
                        cachedArchived.orders = cachedArchived.orders.map((o: WorkOrder) =>
                            o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o
                        );
                        await setOfflineCache('pd_cached_orders_archived', cachedArchived);
                    }

                    showToast('Guardado localmente. Se enviará al recuperar conexión.', 'info');
                } catch (e) {
                    console.error('Error al encolar mutación offline:', e);
                    showToast('Sin conexión. No se ha podido guardar localmente.', 'error');
                }
                return;
            }
            await updateOrderMutation.mutateAsync(updatedOrder);
        },
        [isOnline, queryClient, updateOrderMutation, showToast]
    );

    const deleteOrder = useCallback(
        async (orderId: string) => {
            if (!isOnline) {
                try {
                    await enqueueMutation('deleteOrder', { orderId });
                    queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                        if (!oldData?.pages) return oldData;
                        return {
                            ...oldData,
                            pages: oldData.pages.map((page: OrdersPage) => ({
                                ...page,
                                orders: page.orders.filter((o: WorkOrder) => o.id !== orderId),
                            })),
                        };
                    });
                    showToast('Eliminación guardada localmente. Se aplicará al recuperar conexión.', 'info');
                } catch (e) {
                    console.error('Error al encolar mutación offline:', e);
                    showToast('Sin conexión. No se ha podido guardar localmente.', 'error');
                }
                return;
            }
            await deleteOrderMutation.mutateAsync(orderId);
        },
        [isOnline, queryClient, deleteOrderMutation, showToast]
    );

    const deleteOrders = useCallback(
        async (orderIds: string[]) => {
            if (!isOnline) {
                try {
                    await enqueueMutation('deleteOrders', { orderIds });
                    queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                        if (!oldData?.pages) return oldData;
                        return {
                            ...oldData,
                            pages: oldData.pages.map((page: OrdersPage) => ({
                                ...page,
                                orders: page.orders.filter((o: WorkOrder) => !orderIds.includes(o.id)),
                            })),
                        };
                    });
                    showToast('Eliminaciones guardadas localmente. Se aplicarán al recuperar conexión.', 'info');
                } catch (e) {
                    console.error('Error al encolar mutación offline:', e);
                    showToast('Sin conexión. No se ha podido guardar localmente.', 'error');
                }
                return;
            }
            await deleteOrdersMutation.mutateAsync(orderIds);
        },
        [isOnline, queryClient, deleteOrdersMutation, showToast]
    );

    const deleteEvent = useCallback(
        async (orderId: string, eventId: string) => {
            if (!isOnline) {
                try {
                    await enqueueMutation('deleteEvent', { orderId, eventId });
                    queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                        if (!oldData?.pages) return oldData;
                        return {
                            ...oldData,
                            pages: oldData.pages.map((page: OrdersPage) => ({
                                ...page,
                                orders: page.orders.map((o: WorkOrder) =>
                                    o.id === orderId
                                        ? { ...o, events: (o.events || []).filter((e) => e.id !== eventId) }
                                        : o
                                ),
                            })),
                        };
                    });
                    showToast('Cambio guardado localmente. Se aplicará al recuperar conexión.', 'info');
                } catch (e) {
                    console.error('Error al encolar mutación offline:', e);
                    showToast('Sin conexión. No se ha podido guardar localmente.', 'error');
                }
                return;
            }
            await deleteEventMutation.mutateAsync({ orderId, eventId });
        },
        [isOnline, queryClient, deleteEventMutation, showToast]
    );

    const updateEvent = useCallback(
        async (orderId: string, eventId: string, newText: string) => {
            if (!isOnline) {
                try {
                    await enqueueMutation('updateEvent', { orderId, eventId, newText });
                    queryClient.setQueriesData({ queryKey: queryKeys.orders() }, (oldData: InfiniteData<OrdersPage> | undefined) => {
                        if (!oldData?.pages) return oldData;
                        return {
                            ...oldData,
                            pages: oldData.pages.map((page: OrdersPage) => ({
                                ...page,
                                orders: page.orders.map((o: WorkOrder) =>
                                    o.id === orderId
                                        ? {
                                            ...o,
                                            events: (o.events || []).map((e) =>
                                                e.id === eventId ? { ...e, text: newText } : e
                                            ),
                                        }
                                        : o
                                ),
                            })),
                        };
                    });
                    showToast('Cambio guardado localmente. Se aplicará al recuperar conexión.', 'info');
                } catch (e) {
                    console.error('Error al encolar mutación offline:', e);
                    showToast('Sin conexión. No se ha podido guardar localmente.', 'error');
                }
                return;
            }
            await updateEventMutation.mutateAsync({ orderId, eventId, newText });
        },
        [isOnline, queryClient, updateEventMutation, showToast]
    );

    return {
        createOrder,
        updateOrder,
        deleteOrder,
        deleteOrders,
        assignOrder,
        deleteEvent,
        updateEvent,
    };
}
